import React from 'react'
import styled from '@emotion/styled'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router'
import { AppHeaderLogo } from '@mobi/component-library/AppHeader/AppHeaderLogo'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { callCenterPhoneNumber } from '@mobi/utils'
import { colors, measurements, spacing } from '@mobi/component-library/Theme/Common'
import { routes, trackSignUpGoBackHomePage, trackSignUpNeedAssistance } from '../Utils'
import { useHistory } from '@mobi/account/Hooks/useHistory'
import { useSelector } from 'react-redux'
import { getSignUpState } from '../Store'

const backAndHomeDisabledRoutes = [
  routes.password,
  routes.phoneNumberVerification,
  routes.security,
  routes.depositLimit,
]

const onlyBackDisabledRoutes = [
  routes.contactDetails,
  routes.phoneNumberVerification,
  routes.depositBonus,
  routes.signUpComplete,
  routes.contactDetailsError,
  routes.signUpUnsuccessful,
  routes.failGeneral,
]

export const SignUpPageHeader = (): JSX.Element => {
  const history = useHistory()
  const location = useLocation()
  const navigate = useNavigate()
  const isFetching = useIsFetching()
  const isMutating = useIsMutating()
  const state = useSelector(getSignUpState)

  const isProcessing = isFetching > 0 || isMutating > 0

  const route = location.pathname
  const isBackDisabled =
    backAndHomeDisabledRoutes.includes(route) || onlyBackDisabledRoutes.includes(route)
  const isHomeDisabled = backAndHomeDisabledRoutes.includes(route)

  return (
    <SignUpPageHeaderStyled>
      <BackNavAndLogoContainer>
        <Button
          aria-label='navigate back'
          isIconOnlyButton
          onClick={() => {
            if (isBackDisabled || isProcessing) return

            const hasPreviousPage = location.key !== 'default'
            if (hasPreviousPage) {
              navigate(-1)
            } else {
              navigate('/')
            }
          }}
          color='tertiary_color'
          size='sm'
          style={{ display: isBackDisabled ? 'none' : 'flex' }}
        >
          <Icon name='SolidChevronLeft' color={colors.white} />
        </Button>

        <AppHeaderLogo
          onClick={e => {
            e.preventDefault()
            if (isHomeDisabled || isProcessing) return
            trackSignUpGoBackHomePage(location.pathname, state?.hasUserStartedSignup ?? false)
            navigate('/')
          }}
          compactBreakpoint='px70'
        />
      </BackNavAndLogoContainer>

      <Button
        onClick={() => {
          if (isProcessing) return

          history.push(callCenterPhoneNumber)
          trackSignUpNeedAssistance()
        }}
        color='secondary_color'
        size='sm'
      >
        Need Help?
        <Icon name='LinePhoneCall01' />
      </Button>
    </SignUpPageHeaderStyled>
  )
}

const SignUpPageHeaderStyled = styled.header({
  backgroundColor: colors.studio[700],
  color: colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `0 ${spacing.sm}`,
  boxSizing: 'border-box',
  height: measurements.mobi.headerHeight,
})

const BackNavAndLogoContainer = styled.div({
  display: 'flex',
  justifyContent: 'start',
  '> :last-child': {
    paddingLeft: spacing.smx2,
  },
})
